import React, {useEffect, useState, memo, useMemo} from 'react';
import Button from '@material-ui/core/Button';
import {createStyles, makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import {Checkbox} from "@material-ui/core";
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from "@material-ui/icons/Delete";
import { useSelector, useDispatch } from 'react-redux';
import {
  EnhancedTable,
  DraggableDialog,
  PromptDialog
} from '../components';
import { PythonExecutable, trackToolUsage } from '../executable';
import { fse, getTemporaryFile, registerEvent} from '../electron-modules';
import {
  getMatrixValues,
  saveMatrixFile,
  saveProject,
  writeToString
} from "../utilities";
import path from 'path';
import {
  changeBasicAdjustments,
  changeCameraTransform,
  selectBasicAdjustments,
  selectCameraTransform,
  selectActiveCamera
} from '../redux/camera-slice';
import { selectAllAssets, getCameraFileAssets } from '../redux/assets-slice';
import LocalScene, { SceneCoordinate } from '../viewer/js/projections';
import { toast } from '../app';
import { useTranslation } from 'react-i18next';
import { ImageObservationIcon, ObservationError, PointObservationIcon } from './observations';
import { AlignerObservation } from '../viewer/js/data-aligner';
import { UnitConverter } from '../viewer/js/utilities';
import {
  useDialog,
  useUniqueProjectID,
  useViewer,
  useActiveTool
} from '../hooks';
import { useTableState } from '../hooks/use-table-state';

const converter = new UnitConverter();

const highCutoff = 1.0;
const lowCutoff = 0.5;
const requiredRows = 5;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    minHeightContent: {
      minHeight: "100px"
    },
    toggleOptionParent: {
      margin: theme.spacing(0),
      padding: theme.spacing(0),
      boxShadow: "0px 10px 10px 2px",
      justifyContent: "center"
    },
    toggleOptionCheckbox: {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5)
    },
    toggleOptionFlex: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    userWarning: {
      padding: theme.spacing(3)
    },
    box: {
      padding: theme.spacing(4)
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    adornedEnd: {
      paddingRight: theme.spacing(0)
    },
    utmButton: {
      marginLeft: theme.spacing(1)
    },
    selectMargin: {
      margin: theme.spacing(1),
    },
    button: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1)
    },
    dropdown: {
      minWidth: 200,
    },
    formControl: {
      width: '100%'
    },
    buttonDiv: {
      textAlign: "right"
    },
    success: {
      color: theme.palette.success.main
    },
    warning: {
      color: theme.palette.warning.main
    },
    error: {
      color: theme.palette.error.main
    },
    info: {
      color: theme.palette.info.main
    },
    assetCheckbox: {
      position: "relative",
      transform: "translateY(0%)"
    },
    checkboxPadding: {
      padding: "0px"
    },
    matrixValuesParent: {
      display: "flex",
      paddingTop: theme.spacing(2),
      justifyContent: "space-evenly"
    },
    matrixValues: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center"
    }
  }),
);

interface ResidualErrorProps {
  value: number
}

const ResidualError = (props: ResidualErrorProps) => {
  return <ObservationError
    value={props.value}
    lowCutoff={lowCutoff}
    highCutoff={highCutoff}
  />;
};

interface ImageAlignerAdvancedProps {
  observations: AlignerObservation[];
}

export const ImageAlignerAdvanced = memo((props: ImageAlignerAdvancedProps) => {
  const {observations} = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const tableState = useTableState();
  const alignerAdvDialog = useDialog();
  const removeDialog = useDialog();
  const deleteDialog = useDialog();
  const uniqueProjectID = useUniqueProjectID();
  const {activeToolOpen, setActiveToolOpen} = useActiveTool();

  const [calculating, setCalculating] = useState(false);
  const [alignerMatrixPath, setAlignerMatrixPath] = useState(null);
  const [closePrompt, setClosePrompt] = useState(false);
  const [alignPosition, setAlignPosition] = useState(true);
  const [alignRotation, setAlignRotation] = useState(true);
  const [newRotation, setNewRotation] = useState([0,0,0]);
  const [newOffset, setNewOffset] = useState([0,0,0]);
  const units = LocalScene.dataProjectionUnits;

  const cameraTransformPath = useSelector(selectCameraTransform);
  const currentCamera = useSelector(selectActiveCamera);
  const basicAdjustments = useSelector(selectBasicAdjustments);

  const assets = useSelector(selectAllAssets);
  const camerasFiles = getCameraFileAssets(assets);

  const exportCSV = async () => {
    let observationsCSV = [];
    let tmpObservationPath = getTemporaryFile('csv');

    let encompassCSV = [];
    let tmpEncompassPath = getTemporaryFile('csv');

    encompassCSV.push([
      'filename', 'X', 'Y', 'Z', 'Roll', 'Pitch', 'Yaw'
    ]);

    observationsCSV.push([
      'filename', 'x_img', 'y_img', 'z_img',
      'x_ground', 'y_ground', 'z_ground'
    ]);

    observations.forEach(observation => {
      const {imageObs, pointObs, name, pose} = observation;

      observationsCSV.push([
        name,
        imageObs.x, imageObs.y, imageObs.z,
        pointObs.x, pointObs.y, pointObs.z
      ]);

      encompassCSV.push(pose);
    });

    const observationsCSVContent = writeToString(observationsCSV);
    await fse.writeFile(tmpObservationPath, observationsCSVContent);

    const encompassCSVContent = writeToString(encompassCSV);
    await fse.writeFile(tmpEncompassPath, encompassCSVContent);

    return {tmpObservationPath, tmpEncompassPath};
  };

  const runCalculation = async () => {
    const {tmpObservationPath, tmpEncompassPath} = await exportCSV();

    let exe = new PythonExecutable();
    let commands = [
      "-p", "camera_aligner_advanced",
      "--csv_obs", tmpObservationPath,
      "--csv_encompass", tmpEncompassPath,
      "--sulphur", true,
      "--align_position", alignPosition,
      "--align_rotation", alignRotation,
      "--units", LocalScene.dataProjectionUnits
    ];

    let isConverted = false;
    let largeResiduals = false;

    let camPoseEntry = observations[0].pose;
    let initialPath = path.dirname(camPoseEntry[0]);
    let nameCorrection = path.basename(initialPath);
    let correctionName = nameCorrection + "_correction_matrix.4x4";
    let correctionPath = path.join(initialPath, correctionName);

    setCalculating(true);
    trackToolUsage("aligner-advanced");

    exe.run({
      commands,
      saveLogs: true,
      onLine: jsonData => {
        let residualsTemp = parseResiduals(jsonData);
        if (residualsTemp.length === 0) return;

        isConverted = true;
        viewer.setErrorsCameraAligner(residualsTemp);
        largeResiduals = residualsTemp
          .filter(x => x > highCutoff).length > 0;
      },
      onClose: () => {
        setCalculating(false);

        if (!isConverted) {
          return toast.error(t("toast.aligner-failed"));
        }

        toast.success(t("toast.alignment-completed"));
        setAlignerMatrixPath(correctionPath);

        if (largeResiduals) {
          toast.warning(t("toast.data-aligner-adv-large-error"));
        }
      }
    });

  };

  const parseResiduals = (jsonData) => {
    let resArray = [];

    if (jsonData) {
      jsonData.forEach(res => {
        let rounded = Math.round(parseFloat(res) * 100) / 100;
        resArray.push(rounded);
      });
    }

    return resArray;
  };

  const addNewRow = () => {
    viewer.generateAdvancedAlignerRows(1);
    if (!calculated) return;
    onCancel();
  };

  const onDelete = (row) => {
    viewer.deleteRowCameraAligner(row.id);
    if (!calculated) return;
    onCancel();
  };

  const onClick = (row, index) => {
    if (index !== 0) return;

    const observation = observations.find(x => x.id === row.id);
    if (!observation) return;

    const camID = observation.imageIdentifier;
    const position = observation.imageMesh?.position;

    if (!position) return;

    const lookat = new SceneCoordinate(position).toDataProjection();
    viewer.loadImage(camID, {lookat});
    viewer.flashObservations(observation.id);
  };

  const onCancel = () => {
    setAlignerMatrixPath(null);
    viewer.clearErrorsCameraAligner();
    dispatch(changeCameraTransform(null));
  };

  const onClearAlignments = () => {
    dispatch(changeBasicAdjustments({}));
    viewer?.resetBasicAlignerCamera();
    saveProject(false);
    onCancel();
  };

  const confirmAlignment = () => {
    dispatch(changeCameraTransform(alignerMatrixPath));
  };

  const saveAlignmentFile = () => {
    saveMatrixFile(cameraTransformPath);
  };

  const handleAlignPositionCheck = () => {
    setAlignPosition(!alignPosition);
    if (!calculated) return;
    onCancel();
  };

  const handleAlignRotationCheck = () => {
    setAlignRotation(!alignRotation);
    if (!calculated) return;
    onCancel();
  };

  const checkClose = () => {
    if (!hasAlignment && !isTableEmpty()) {
      setClosePrompt(true);
    } else {
      alignerAdvDialog.handleClose();
    }
  };

  const onImageClick = (id) => {
    viewer.setCurrentObservation(id, 'image');
    if (!calculated) return;
    onCancel();
  };

  const onSceneClick = (id) => {
    viewer.setCurrentObservation(id, 'point');
    if (!calculated) return;
    onCancel();
  };

  const isTableEmpty = () => {
    const filledRows = observations.filter(observation => {
      return observation.pointObs || observation.imageObs;
    });

    return filledRows.length === 0;
  };

  const isTableFilled = () => {
    const filledRows = observations.filter(observation => {
      return observation.pointObs && observation.imageObs;
    });

    return filledRows.length === observations.length;
  };

  const getTransformValues = async (filePath) => {
    const cameraTransform = await getMatrixValues(filePath);

    let offset;
    if (cameraTransform.success) {
      offset = [
        converter.convert(cameraTransform.offset.x, "m", units, 2),
        converter.convert(cameraTransform.offset.y, "m", units, 2),
        converter.convert(cameraTransform.offset.z, "m", units, 2)
      ];
    } else {
      offset = [0,0,0];
    }

    let rotation;
    if (cameraTransform.success) {
      rotation = [
        cameraTransform.rotation.x.toFixed(2),
        cameraTransform.rotation.y.toFixed(2),
        cameraTransform.rotation.z.toFixed(2)
      ];
    } else {
      rotation = [0,0,0];
    }

    setNewRotation(rotation);
    setNewOffset(offset);
  };

  useEffect(() => {
    /** Keep track if a tool is currently being used */
    setActiveToolOpen(alignerAdvDialog.open);
  }, [alignerAdvDialog.open]);

  useEffect(() => {
    /** Close dialog when new project is loaded */
    alignerAdvDialog.handleClose();
  }, [uniqueProjectID]);

  useEffect(() => {
    registerEvent("open-image-aligner-adv", () => {
      if (activeToolOpen) return toast.warning(t('toast.one_tool_only'));
      alignerAdvDialog.handleOpen();
    });
  }, [activeToolOpen]);

  useEffect(() => {
    const aligned = cameraTransformPath !== null;
    const removed = currentCamera === null;
    const noCameras = camerasFiles.length === 0;

    if (aligned && removed && noCameras) {
      removeDialog.handleOpen();
    }
  }, [currentCamera]);

  useEffect(() => {
    getTransformValues(alignerMatrixPath);
  }, [alignerMatrixPath]);

  useEffect(() => {
    if (!viewer) return;

    viewer.toggleCameraAligner(alignerAdvDialog.open);
    if (!alignerAdvDialog.open) return;

    viewer.generateAdvancedAlignerRows(requiredRows);
  }, [viewer, alignerAdvDialog.open]);

  const getRows = (observations, calculating) => {
    return observations.map(observation => {
      const disabled = calculating;

      const imageSelected = !!observation.imageObs;
      const imageClicked = observation.imageSelected;

      const pointSelected = !!observation.pointObs;
      const pointClicked = observation.pointSelected;

      const imageObs = (
        <ImageObservationIcon
          selected={imageSelected}
          clicked={imageClicked}
          title={t('observation.add_image_observation')}
          onClick={() => {
            if (disabled) return;
            onImageClick(observation.id);
          }}
        />
      );

      const pointObs = (
        <PointObservationIcon
          selected={pointSelected}
          clicked={pointClicked}
          title={t('observation.add_point_observation')}
          onClick={() => {
            if (disabled) return;
            onSceneClick(observation.id);
          }}
        />
      );

      const error = (
        <ResidualError value={observation.error}/>
      );

      return {
        id: observation.id,
        name: t(observation.name),
        img_obs: imageObs,
        pc_obs: pointObs,
        error,
        disabled
      };
    });
  };

  const calculated = !!alignerMatrixPath;
  const hasBasicAlignment = Object.keys(basicAdjustments).length !== 0;
  const hasAdvancedAlignment = !!cameraTransformPath;
  const hasAlignment = hasBasicAlignment || hasAdvancedAlignment;

  const canSubmit = isTableFilled()
    && observations.length >= requiredRows
    && (alignPosition || alignRotation)
    && !calculating;

  const columns = [
    {
      id: 'name',
      label: t("data-aligner-adv.filename"),
      pointer: true,
      wordBreak: true
    },
    {
      id: 'img_obs',
      label: t("data-aligner-adv.image-control"),
      center: true,
      sortable: false
    },
    {
      id: 'pc_obs',
      label: t("data-aligner-adv.point-control"),
      center: true,
      sortable: false
    },
    {
      id: 'error',
      label: t("data-aligner-adv.error") + '(\u00B0)',
      numeric: true,
      center: true
    }
  ];

  const rows = useMemo(() => {
    return getRows(observations, calculating);
  }, [observations, calculating]);

  const actions = [
    {
      title: t("buttons.delete"),
      icon: <DeleteIcon fontSize="small"/>,
      onClick: (row) => {
        deleteDialog.handleOpen(row);
      }
    }
  ];

  return (
    <React.Fragment>

      <DraggableDialog
        open={alignerAdvDialog.open}
        title={t("data-aligner-adv.title")}
        onClose={checkClose}
      >
        {/** Clear previous alignment */}
        {hasAlignment && (<React.Fragment>
          <DialogContent className={classes.minHeightContent}>
            <Typography variant="caption" className={classes.warning}>
              {t("data-aligner-adv.have-set-alignment")}
            </Typography>
          </DialogContent>

          <DialogActions>

            {/** Download saved alignment matrix */}
            {hasAdvancedAlignment && (<React.Fragment>
              <Button onClick={saveAlignmentFile} color="primary">
                {t("data-aligner-adv.save-camera-matrix")}
              </Button>
            </React.Fragment>)}

            {/** Clear all previous alignments */}
            <Button onClick={onClearAlignments} color="secondary">
              {t("data-aligner-adv.clear-alignment")}
            </Button>

          </DialogActions>
        </React.Fragment>)}

        {/** Standard conditions */}
        {!hasAlignment && (<React.Fragment>
          <DialogContent style={{
            display: "flex",
            flexDirection: "column"
          }}>
            <Typography
              variant="caption"
              style={{marginBottom: theme.spacing(2)}}
            >
              {t("data-aligner-adv.select-at-least", {requiredRows})}
            </Typography>

            <EnhancedTable
              state={tableState}
              columns={columns}
              rows={rows}
              actions={actions}
              onClick={onClick}
            />

          </DialogContent>

          {calculated && (<div className={classes.matrixValuesParent}>
            <Typography variant="caption" className={classes.matrixValues}>
              <b>{t('data-aligner-adv.matrix_offset_units', {units})}: </b> [{newOffset[0]}, {newOffset[1]}, {newOffset[2]}]
            </Typography>

            <Typography variant="caption" className={classes.matrixValues}>
              <b>{t('data-aligner-adv.matrix_rotation')}: </b> [{newRotation[0]}, {newRotation[1]}, {newRotation[2]}]
            </Typography>
          </div>)}

          <DialogActions>
            {/** Add new observation row */}
            <Button disabled={calculating} onClick={addNewRow}>
              {t("data-aligner-adv.add-entry")}
            </Button>

            {/** Cancel calculated alignment */}
            {calculated && (<React.Fragment>
              <Button onClick={onCancel} color="secondary">
                {t('data-aligner-adv.clear-alignment')}
              </Button>
            </React.Fragment>)}

            {/** Run python calculations */}
            {!calculated && (<React.Fragment>
              <Button disabled={!canSubmit} onClick={runCalculation}>
                {t("data-aligner-adv.run-alignment-correction")}
              </Button>
            </React.Fragment>)}

            {/** Confirm alignment */}
            {calculated && (<React.Fragment>
              <Button onClick={confirmAlignment} color="primary">
                {t("data-aligner-adv.apply-alignment-correction")}
              </Button>
            </React.Fragment>)}
          </DialogActions>

          <DialogActions className={classes.toggleOptionParent}>
            {/* Calculate lever arm */}
            <div className={classes.toggleOptionFlex}>
              <Typography variant="caption">
                {t('data-aligner-adv.allow_offset')}
              </Typography>
              <Checkbox
                disabled={calculating}
                checked={alignPosition}
                onChange={handleAlignPositionCheck}
                classes={{root: classes.toggleOptionCheckbox}}
                size="small"
              />
            </div>

            {/* Calculate rotation */}
            <div className={classes.toggleOptionFlex}>
              <Typography variant="caption">
                {t('data-aligner-adv.allow_rotation')}
              </Typography>
              <Checkbox
                disabled={calculating}
                checked={alignRotation}
                onChange={handleAlignRotationCheck}
                classes={{root: classes.toggleOptionCheckbox}}
                size="small"
              />
            </div>
          </DialogActions>

        </React.Fragment>)}

      </DraggableDialog>

      {/* Delete Dialog */}
      <PromptDialog
        open={deleteDialog.open}
        onCancel={deleteDialog.handleClose}
        onSubmit={() => {
          onDelete(deleteDialog.data);
          deleteDialog.handleClose();
        }}
        title={t("enhanced-table.prompt-delete-title")}
        prompt={t('data-aligner-adv.do_you_want_to_delete_this_observation')}
        button={t('buttons.delete')}
      />

      <PromptDialog
        open={closePrompt}
        onCancel={() => setClosePrompt(false)}
        onSubmit={() => {
          alignerAdvDialog.handleClose();
          setClosePrompt(false);
        }}
        title={t("data-aligner-adv.prompt-close-aligner-title")}
        prompt={t("data-aligner-adv.prompt-close-aligner-text")}
        button={t('buttons.close')}
      />

      <PromptDialog
        open={removeDialog.open}
        onCancel={removeDialog.handleClose}
        onSubmit={() => {
          dispatch(changeCameraTransform(null));
          removeDialog.handleClose();
        }}
        title={t("data-aligner-adv.prompt-remove-matrix-title")}
        prompt={t("data-aligner-adv.prompt-remove-matrix-text")}
        button={t('buttons.remove-matrix')}
      />
    </React.Fragment>
  );
});
